/**
 * PDP add-ons: Add-to-expand panel(s), variant links, qty stepper, bundled add.
 * Supports multiple roots per section. Legacy WetLok data-* aliases still work.
 */
;(() => {
  if (window.__pdpAddonRecommendationBound) return
  window.__pdpAddonRecommendationBound = true

  const ROOT_SEL =
    '[data-pdp-addon-root][data-pdp-addon-context="pdp"], [data-wetlok-root][data-wetlok-context="pdp"]'

  function isTileVariant(variant) {
    if (!variant) return false
    const opts = [variant.option1, variant.option2, variant.option3].filter(
      (o) => o != null && o !== ''
    )
    return opts.some((o) => /tile/i.test(String(o)))
  }

  function getPdpRoots(sectionId) {
    if (!sectionId) return []
    return Array.from(document.querySelectorAll(ROOT_SEL)).filter(
      (root) => String(root.dataset.sectionId) === String(sectionId)
    )
  }

  function isPanelExpanded(root) {
    return (
      root?.dataset.pdpAddonExpanded === 'true' ||
      root?.dataset.wetlokExpanded === 'true'
    )
  }

  function getSelectedVariantId(root) {
    const selected = root.querySelector(
      '[data-pdp-addon-variant-input]:checked, [data-wetlok-variant-input]:checked'
    )
    if (selected) return parseInt(selected.value, 10)
    const fallback = parseInt(root.dataset.variantId, 10)
    return Number.isFinite(fallback) ? fallback : null
  }

  function qtyInput(root) {
    return root.querySelector('[data-pdp-addon-qty], [data-wetlok-tubs]')
  }

  function readQty(root) {
    const input = qtyInput(root)
    if (!input) return 0
    return Math.max(0, parseInt(input.value, 10) || 0)
  }

  function setQty(root, qty) {
    const input = qtyInput(root)
    if (!input) return
    const min = parseInt(input.min, 10) || 0
    const next = Math.max(min, parseInt(String(qty), 10) || 0)
    input.value = String(next)
    syncHint(root)
    syncQtyButtons(root)
  }

  function syncHint(root) {
    const hint = root.querySelector('[data-pdp-addon-hint], [data-wetlok-hint]')
    if (!hint) return
    hint.hidden = !isPanelExpanded(root)
  }

  function syncQtyButtons(root) {
    const input = qtyInput(root)
    const minus = root.querySelector(
      '[data-pdp-addon-qty-minus], [data-wetlok-qty-minus]'
    )
    if (!input || !minus) return
    const value = readQty(root)
    const min = parseInt(input.min, 10) || 0
    minus.classList.toggle('disabled', value <= min)
  }

  function collapsePanel(root) {
    if (!root) return
    root.dataset.pdpAddonExpanded = 'false'
    root.dataset.wetlokExpanded = 'false'

    const panel = root.querySelector(
      '[data-pdp-addon-panel], [data-wetlok-panel]'
    )
    const expandBtn = root.querySelector(
      '[data-pdp-addon-expand], [data-wetlok-expand]'
    )

    if (panel) panel.hidden = true
    if (expandBtn) {
      expandBtn.hidden = false
      expandBtn.disabled = false
      expandBtn.setAttribute('aria-expanded', 'false')
    }

    setQty(root, 0)
  }

  function expandPanel(root) {
    if (!root || isPanelExpanded(root)) return

    root.dataset.pdpAddonExpanded = 'true'
    root.dataset.wetlokExpanded = 'true'

    const panel = root.querySelector(
      '[data-pdp-addon-panel], [data-wetlok-panel]'
    )
    const expandBtn = root.querySelector(
      '[data-pdp-addon-expand], [data-wetlok-expand]'
    )

    if (panel) panel.hidden = false
    if (expandBtn) {
      expandBtn.hidden = true
      expandBtn.disabled = true
      expandBtn.setAttribute('aria-expanded', 'true')
    }

    setQty(root, 0)

    const variantId = getSelectedVariantId(root)
    if (variantId) root.dataset.variantId = String(variantId)
  }

  function hideOnTileEnabled(root) {
    return root.dataset.hideOnTile !== 'false'
  }

  function setRootVisible(root, visible) {
    if (!root) return
    root.classList.toggle('hidden', !visible)
    if (!visible) {
      root.dataset.isTileVariant = 'true'
      collapsePanel(root)
    } else {
      delete root.dataset.isTileVariant
    }
  }

  function isRootActive(root) {
    if (!root) return false
    if (root.classList.contains('hidden')) return false
    if (root.dataset.isTileVariant === 'true') return false
    if (!isPanelExpanded(root)) return false
    return true
  }

  /**
   * Active add-on lines to bundle with host ATC.
   * @param {string} sectionId
   * @returns {{ variantId: number, quantity: number }[]}
   */
  window.getPdpAddonSelections = (sectionId) => {
    const selections = []
    for (const root of getPdpRoots(sectionId)) {
      if (!isRootActive(root)) continue
      const quantity = readQty(root)
      if (quantity < 1) continue
      const variantId = getSelectedVariantId(root)
      if (!variantId) continue
      selections.push({ variantId, quantity })
    }
    return selections
  }

  /**
   * @deprecated Use getPdpAddonSelections. Returns first selection or null.
   * @param {string} sectionId
   * @returns {{ variantId: number, quantity: number } | null}
   */
  window.getWetlokPdpSelection = (sectionId) => {
    const selections =
      typeof window.getPdpAddonSelections === 'function'
        ? window.getPdpAddonSelections(sectionId)
        : []
    return selections[0] || null
  }

  function wirePdp(root) {
    const sectionId = root.dataset.sectionId
    if (!sectionId) return

    const input = qtyInput(root)
    const variantInputs = root.querySelectorAll(
      '[data-pdp-addon-variant-input], [data-wetlok-variant-input]'
    )

    const syncVariantId = () => {
      const variantId = getSelectedVariantId(root)
      if (variantId) root.dataset.variantId = String(variantId)
    }

    const onQtyChange = () => {
      syncQtyButtons(root)
    }

    if (input) {
      input.addEventListener('input', onQtyChange)
      input.addEventListener('change', onQtyChange)
    }

    variantInputs.forEach((el) => {
      el.addEventListener('change', syncVariantId)
    })

    syncVariantId()
    onQtyChange()

    if (
      typeof subscribe !== 'undefined' &&
      typeof PUB_SUB_EVENTS !== 'undefined'
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const data = event?.data
        if (!data || String(data.sectionId) !== String(sectionId)) return
        if (!hideOnTileEnabled(root)) return
        const tile = isTileVariant(data.variant)
        setRootVisible(root, !tile)
        if (!tile) {
          collapsePanel(root)
        }
        syncVariantId()
        onQtyChange()
      })
    }
  }

  document.addEventListener('click', (e) => {
    const expandBtn = e.target.closest(
      '[data-pdp-addon-expand], [data-wetlok-expand]'
    )
    if (expandBtn) {
      const root = expandBtn.closest(ROOT_SEL)
      if (!root || root.classList.contains('hidden')) return
      if (root.dataset.isTileVariant === 'true') return
      e.preventDefault()
      expandPanel(root)
      return
    }

    const minus = e.target.closest(
      '[data-pdp-addon-qty-minus], [data-wetlok-qty-minus]'
    )
    const plus = e.target.closest(
      '[data-pdp-addon-qty-plus], [data-wetlok-qty-plus]'
    )
    const button = minus || plus
    if (!button) return

    const root = button.closest(ROOT_SEL)
    if (!root || !isRootActive(root)) return
    if (button.classList.contains('disabled')) return

    e.preventDefault()

    const input = qtyInput(root)
    const min = parseInt(input?.min, 10) || 0

    if (minus) {
      setQty(root, Math.max(min, readQty(root) - 1))
    } else {
      setQty(root, readQty(root) + 1)
    }
  })

  function initPdpRoots() {
    document.querySelectorAll(ROOT_SEL).forEach(wirePdp)
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPdpRoots)
  } else {
    initPdpRoots()
  }
})()
